import {
  applicantSection,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { bankAccountField, childAddressFields, childIdentityFields } from "modules/ebiForms/definitions/sharedEducationSafety";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * D7. Wniosek o zapewnienie bezpłatnego transportu i opieki albo o zwrot kosztów dowozu dziecka (ucznia)
 * niepełnosprawnego — art. 32 ust. 6, art. 39 ust. 4 i art. 39a ustawy — Prawo oświatowe; stawkę za 1 km ustala rada
 * gminy, wymagania co do dokumentów — zarządzenia gmin. Brak wzoru ogólnopolskiego; układ części „zwrot kosztów” jak
 * wniosek Krakowa o zwrot kosztów przejazdu niepełnosprawnego dziecka (https://plikimpi.krakow.pl/zalacznik/454525 —
 * pobrany 2026-10-22): trasa (zamieszkanie — szkoła — praca rodzica, przewóz powierzony innemu podmiotowi), dziecko,
 * placówka, okres, odległości a¹/a²/b, adres pracy rodzica, orzeczenie. Wzór krakowski ma klauzulę art. 233 KK, ale
 * ustawa nie przewiduje jej w tej sprawie — zwykłe oświadczenie o zgodności danych. Zwrot kosztów następuje na podstawie
 * umowy z gminą (nie decyzji); rozliczenia miesięczne to kolejny obieg.
 */
const TRANSPORT: EbiCondition = { field: "rodzajWniosku", equals: "dowoz" };
const REFUND: EbiCondition = { field: "rodzajWniosku", equals: "zwrot" };
const OWN_CAR: EbiCondition = { all: [REFUND, { field: "sposobDowozu", equals: "samochod" }] };
const BY_ROAD: EbiCondition = { all: [REFUND, { field: "sposobDowozu", in: ["samochod", "innyPodmiot"] }] };
const VIA_WORK: EbiCondition = { all: [BY_ROAD, { field: "trasa", equals: "pracaRodzica" }] };

const D7: IEbiFormDefinition = {
  code: "D7",
  version: 1,
  title: "Wniosek o zapewnienie dowozu albo zwrot kosztów dowozu ucznia niepełnosprawnego",
  category: EEbiFormCategory.Education,
  office: EEbiFormOffice.MunicipalUnit,
  description:
    "Bezpłatny transport i opieka w czasie przewozu dziecka niepełnosprawnego do przedszkola, szkoły lub ośrodka albo zwrot kosztów dowozu zapewnianego przez rodziców.",
  keywords: ["dowóz", "transport", "uczeń niepełnosprawny", "zwrot kosztów", "kształcenie specjalne", "przedszkole", "szkoła"],
  organ: "Wójt / Burmistrz / Prezydent Miasta (jednostka obsługi oświaty)",
  legalBasis:
    "art. 32 ust. 6, art. 39 ust. 4 i art. 39a ustawy z dnia 14 grudnia 2016 r. — Prawo oświatowe; uchwała rady gminy w sprawie stawki za 1 km przebiegu pojazdu",
  request: "Wnoszę o zapewnienie bezpłatnego transportu i opieki w czasie przewozu albo o zwrot kosztów przewozu dziecka — zgodnie z rodzajem wniosku wskazanym poniżej.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty; pełnomocnictwo — 17 zł.",
  variantField: "rodzajWniosku",
  variants: {
    dowoz: { title: "Wniosek o zapewnienie bezpłatnego transportu i opieki ucznia niepełnosprawnego" },
    zwrot: { title: "Wniosek o zwrot kosztów dowozu ucznia niepełnosprawnego" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj wniosku",
      fields: [
        {
          code: "rodzajWniosku",
          type: "radio",
          label: "Wnoszę o",
          required: true,
          options: [
            { value: "dowoz", label: "zapewnienie bezpłatnego transportu i opieki w czasie przewozu (dowóz organizuje gmina)" },
            { value: "zwrot", label: "zwrot kosztów przewozu dziecka i opiekuna (dowóz zapewniam ja jako rodzic)" },
          ],
        },
      ],
    },
    applicantSection("Wnioskodawca — rodzic lub opiekun prawny"),
    representationSection(),
    {
      code: "dziecko",
      title: "Dane dziecka (ucznia)",
      fields: [...childIdentityFields("dziecko"), ...childAddressFields("dziecko")],
    },
    {
      code: "placowka",
      title: "Przedszkole, szkoła lub ośrodek",
      fields: [
        {
          code: "rodzajPlacowki",
          type: "select",
          label: "Rodzaj placówki",
          required: true,
          options: [
            { value: "przedszkole", label: "przedszkole, oddział przedszkolny lub inna forma wychowania przedszkolnego" },
            { value: "podstawowa", label: "szkoła podstawowa" },
            { value: "ponadpodstawowa", label: "szkoła ponadpodstawowa" },
            { value: "osrodek", label: "ośrodek rewalidacyjno-wychowawczy lub inny ośrodek" },
          ],
        },
        { code: "placowkaNazwa", type: "text", label: "Nazwa placówki", required: true, maxLength: 300 },
        { code: "placowkaAdres", type: "text", label: "Adres placówki", required: true, maxLength: 300 },
        { code: "okresOd", type: "date", label: "Wniosek dotyczy okresu od", required: true, width: "half" },
        { code: "okresDo", type: "date", label: "do", required: true, width: "half" },
      ],
    },
    {
      code: "dowoz",
      title: "Zapewnienie dowozu",
      visibleWhen: TRANSPORT,
      fields: [
        {
          code: "informacjeOrganizacyjne",
          type: "textarea",
          label: "Informacje ważne dla organizacji przewozu",
          hint: "Np. godziny zajęć, adres odbioru dziecka, jeśli inny niż adres zamieszkania. Nie opisuj stanu zdrowia — orzeczenie dołącz jako załącznik.",
          maxLength: 2000,
        },
      ],
    },
    {
      code: "zwrot",
      title: "Zwrot kosztów dowozu",
      visibleWhen: REFUND,
      fields: [
        {
          code: "sposobDowozu",
          type: "radio",
          label: "Dowóz zapewniam",
          required: true,
          options: [
            { value: "samochod", label: "samochodem, którym dysponuję" },
            { value: "komunikacja", label: "środkami komunikacji publicznej (zwrot kosztów biletów dziecka i opiekuna)" },
            { value: "innyPodmiot", label: "przez inny podmiot, któremu powierzyłam/powierzyłem przewóz (art. 39a ust. 6)" },
          ],
        },
        {
          code: "trasa",
          type: "radio",
          label: "Trasa przewozu",
          required: true,
          visibleWhen: BY_ROAD,
          options: [
            { value: "szkola", label: "miejsce zamieszkania dziecka — placówka" },
            { value: "pracaRodzica", label: "miejsce zamieszkania dziecka — placówka — miejsce pracy rodzica (opiekuna)" },
          ],
        },
        {
          code: "kmA1",
          type: "number",
          label: "a¹) Odległość z miejsca zamieszkania do placówki [km]",
          required: true,
          visibleWhen: BY_ROAD,
          decimals: 2,
          min: 0,
          width: "half",
        },
        {
          code: "kmA2",
          type: "number",
          label: "a²) Odległość z placówki do miejsca pracy rodzica (opiekuna) [km]",
          required: true,
          visibleWhen: VIA_WORK,
          decimals: 2,
          min: 0,
          width: "half",
        },
        {
          code: "kmB",
          type: "number",
          label: "b) Odległość z miejsca zamieszkania do miejsca pracy rodzica (opiekuna), gdyby nie wykonywał przewozu dziecka [km]",
          required: true,
          visibleWhen: VIA_WORK,
          decimals: 2,
          min: 0,
          width: "half",
        },
        { code: "adresPracy", type: "text", label: "Adres miejsca pracy rodzica (opiekuna)", required: true, visibleWhen: VIA_WORK, maxLength: 300 },
        { code: "pojazdMarka", type: "text", label: "Marka i model samochodu", required: true, visibleWhen: OWN_CAR, width: "half", maxLength: 100 },
        { code: "pojazdNrRejestracyjny", type: "text", label: "Numer rejestracyjny", required: true, visibleWhen: OWN_CAR, width: "half", maxLength: 15 },
        {
          code: "przewoznik",
          type: "text",
          label: "Podmiot, któremu powierzono przewóz (nazwa albo imię i nazwisko, adres)",
          required: true,
          visibleWhen: { all: [REFUND, { field: "sposobDowozu", equals: "innyPodmiot" }] },
          maxLength: 300,
        },
        bankAccountField("rachunek", "Numer rachunku bankowego do zwrotu kosztów", { hint: "Do umowy o zwrot kosztów (nieobowiązkowo — możesz podać przy jej zawieraniu)." }),
        feeInfo(
          "infoZwrot",
          "Zwrot kosztów następuje na podstawie umowy zawieranej przez wójta (burmistrza, prezydenta miasta) z rodzicami; stawkę za 1 km przebiegu pojazdu ustala rada gminy. Rozliczenia (np. liczba dni dowozu potwierdzona przez placówkę, bilety) składa się w terminach wskazanych w umowie.",
        ),
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenie",
      fields: [
        {
          code: "oswiadczenieZgodnosc",
          type: "declaration",
          label: "Oświadczam, że dane podane we wniosku są zgodne ze stanem faktycznym.",
          required: true,
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "orzeczenie",
      label: "Orzeczenie o potrzebie kształcenia specjalnego albo o potrzebie zajęć rewalidacyjno-wychowawczych",
      hint: "Wydane przez publiczną poradnię psychologiczno-pedagogiczną (kopia).",
      required: true,
    },
    {
      code: "zaswiadczeniePlacowki",
      label: "Zaświadczenie przedszkola, szkoły lub ośrodka o przyjęciu albo uczęszczaniu dziecka",
      hint: "Wymagane przez większość gmin — sprawdź zarządzenie gminy.",
    },
    {
      code: "orzeczenieNiepelnosprawnosc",
      label: "Orzeczenie o niepełnosprawności",
      hint: "Jeśli wymaga go zarządzenie gminy (nieobowiązkowo).",
    },
    {
      code: "dowodRejestracyjny",
      label: "Dowód rejestracyjny samochodu",
      hint: "Część gmin o niego prosi; jeśli nie jesteś właścicielem samochodu — dołącz także upoważnienie właściciela.",
      visibleWhen: OWN_CAR,
      multiple: true,
    },
    {
      code: "oswiadczeniaPrzewoznika",
      label: "Oświadczenie rodzica o powierzeniu przewozu i oświadczenie przewoźnika",
      hint: "Art. 39a ust. 6 ustawy — Prawo oświatowe.",
      visibleWhen: { all: [REFUND, { field: "sposobDowozu", equals: "innyPodmiot" }] },
      required: true,
      multiple: true,
    },
    {
      code: "pelnomocnictwoDrugiegoRodzica",
      label: "Pełnomocnictwo drugiego rodzica do zawarcia umowy o zwrot kosztów",
      hint: "Jeśli wymaga go gmina (nieobowiązkowo).",
      visibleWhen: REFUND,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default D7;
